import type { Language } from './region';
type Catalog = Record<string, string>;
// One build chunk per language; no runtime translation service or customer data.
const modules = import.meta.glob<{ default: Catalog }>('./locales/*.json');
const catalogs = new Map<string, Catalog>();
const pending = new Map<string, Promise<void>>();
export const hasCatalog = (language: string) => language === 'vi' || catalogs.has(language);
export function loadCatalog(language: Language): Promise<void> {
  if (hasCatalog(language)) return Promise.resolve();
  const existing = pending.get(language); if (existing) return existing;
  const loader = modules[`./locales/${language}.json`];
  if (!loader) return Promise.reject(new Error(`Missing UI catalog: ${language}`));
  const request = loader().then(module => { catalogs.set(language, module.default); patterns.delete(language); }).finally(() => pending.delete(language));
  pending.set(language, request); return request;
}
export type Translate = (message: string | TemplateStringsArray, ...values: unknown[]) => string;
const patterns = new Map<string, { key: string; regex: RegExp; indices: number[] }[]>();
function matchMessage(language: string, message: string) {
  let entries = patterns.get(language);
  if (!entries) {
    entries = Object.keys(catalogs.get(language) || {}).filter(key => /\{\d+\}/.test(key)).sort((a, b) => b.replace(/\{\d+\}/g, '').length - a.replace(/\{\d+\}/g, '').length).map(key => {
      const indices: number[] = [];
      const escaped = key.split(/(\{\d+\})/).map(part => {
        if (/^\{\d+\}$/.test(part)) { indices.push(Number(part.slice(1, -1))); return '(.+?)'; }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }).join('');
      return { key, regex: new RegExp(`^${escaped}$`, 's'), indices };
    });
    patterns.set(language, entries);
  }
  for (const entry of entries) {
    const match = entry.regex.exec(message); if (!match) continue;
    const values: string[] = []; entry.indices.forEach((index, i) => { values[index] = match[i + 1]; });
    return { key: entry.key, values };
  }
}
export function translate(language: Language | string, message: string | TemplateStringsArray, ...values: unknown[]) {
  let key = typeof message === 'string' ? message : Array.from(message).map((part, i) => part + (i < values.length ? `{${i}}` : '')).join('');
  const catalog = catalogs.get(language);
  if (language !== 'vi' && typeof message === 'string' && !catalog?.[key]) {
    const match = matchMessage(language, key); if (match) { key = match.key; values = match.values; }
  }
  const text = language === 'vi' ? key : catalog?.[key] || catalogs.get('en')?.[key] || key;
  return text.replace(/\{(\d+)\}/g, (token, index) => Number(index) < values.length ? String(values[Number(index)] ?? '') : token);
}
